import {
  FieldOperators,
  Insight,
  InsightType,
} from 'src/interfaces/agent.interface';

export const allowedOperatorsByInsightType: Record<
  InsightType,
  FieldOperators[]
> = {
  [InsightType.BOOLEAN]: [FieldOperators.EQ],
  [InsightType.NUMBER]: [
    FieldOperators.EQ,
    FieldOperators.NEQ,
    FieldOperators.GT,
    FieldOperators.GTE,
    FieldOperators.LT,
    FieldOperators.LTE,
  ],
  [InsightType.ENUM]: [FieldOperators.EQ, FieldOperators.NEQ],
  [InsightType.STRING]: [
    FieldOperators.EQ,
    FieldOperators.NEQ,
    FieldOperators.CONTAINS,
    FieldOperators.NOT_CONTAINS,
  ],
};

export type CoerceAndValidateRightResult =
  | { ok: true; right: boolean | number | string }
  | { ok: false; message: string };

export function isAllowedOperatorForInsight(
  item: Insight,
  operator: FieldOperators | undefined,
): boolean {
  if (!operator) return false;
  const allowed = allowedOperatorsByInsightType[item.type] ?? [];
  return allowed.includes(operator);
}

export function coerceAndValidateRight(params: {
  item: Insight;
  operator: FieldOperators | undefined;
  rightInput: string | boolean | null | undefined;
}): CoerceAndValidateRightResult {
  const { item, operator, rightInput } = params;

  if (!isAllowedOperatorForInsight(item, operator)) {
    const allowed = allowedOperatorsByInsightType[item.type] ?? [];
    return {
      ok: false,
      message: `Operator must be one of: ${allowed.join(', ')}`,
    };
  }

  if (item.type === InsightType.BOOLEAN) {
    if (typeof rightInput !== 'boolean') {
      return { ok: false, message: 'Value must be a boolean (True/False).' };
    }
    return { ok: true, right: rightInput };
  }

  if (item.type === InsightType.NUMBER) {
    if (typeof rightInput !== 'string') {
      return { ok: false, message: 'Value must be a number.' };
    }
    const trimmed = rightInput.trim();
    if (!trimmed) return { ok: false, message: 'Value must be a number.' };
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return {
        ok: false,
        message: 'Value must be a number (integer or decimal).',
      };
    }
    return { ok: true, right: parsed };
  }

  if (item.type === InsightType.ENUM) {
    if (typeof rightInput !== 'string') {
      return { ok: false, message: 'Value must be an enum option.' };
    }
    const enumOptions = item.enum_options ?? [];
    if (!enumOptions.includes(rightInput)) {
      return { ok: false, message: 'Value must be one of the enum options.' };
    }
    return { ok: true, right: rightInput };
  }

  if (item.type === InsightType.STRING) {
    if (typeof rightInput !== 'string') {
      return { ok: false, message: 'Value must be a string.' };
    }
    return { ok: true, right: rightInput.trim() };
  }

  return { ok: false, message: 'Unsupported insight type.' };
}

export function sortInsightsForSelect(insights: Insight[]): Insight[] {
  return [...insights].sort((a, b) =>
    (a.label || a.id)
      .toLowerCase()
      .localeCompare((b.label || b.id).toLowerCase()),
  );
}
